import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { TERMINAL_JOB_STATES } from '@cp/shared';
import { sitesApi } from '@/api/sites';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import { useSiteJobs } from '@/hooks/useJobs';
import type { CaptureTemplateRequest, CreateGeoSiteRequest, EnqueueResult, JobDetail, StructureStringsRequest } from '@/types/api';

/**
 * Template builds and new GEOs (ADR-0047): the wizard's reads and writes, a
 * new site's creation followed step by step, and a site's structure.
 */

type Queued = EnqueueResult & { siteId: string };

export function useFreeDomains({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: queryKeys.freeDomains, queryFn: () => sitesApi.freeDomains(), enabled, staleTime: 60_000 });
}

export function useCreateGeo(callbacks?: MutationCallbacks<Queued, { input: CreateGeoSiteRequest; key: string }>) {
  return useApiMutation(({ input, key }) => sitesApi.createGeo(input, key), () => [queryKeys.sites.all, queryKeys.jobs.all, queryKeys.freeDomains], callbacks);
}

/** Asked again every two seconds while the creation is under way. */
export function useSiteSetup(siteId: string | null) {
  return useQuery({
    queryKey: queryKeys.siteSetup(siteId ?? ''),
    queryFn: () => sitesApi.setup(siteId!),
    enabled: siteId !== null,
    refetchInterval: (query) => {
      const state = query.state.data?.state;
      return state === 'completed' || state === 'failed' ? false : 2_000;
    },
  });
}

export function useSiteStructure(siteId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: queryKeys.siteStructure(siteId), queryFn: () => sitesApi.structure(siteId), enabled });
}

export function useSaveStructure(siteId: string, callbacks?: MutationCallbacks<EnqueueResult, { input: StructureStringsRequest; key: string }>) {
  return useApiMutation(
    ({ input, key }) => sitesApi.saveStructure(siteId, input, key),
    () => [queryKeys.siteStructure(siteId), queryKeys.site(siteId), queryKeys.jobs.site(siteId)],
    callbacks,
  );
}

export function useLaunch(siteId: string, callbacks?: MutationCallbacks<EnqueueResult, string>) {
  return useApiMutation((key) => sitesApi.launch(siteId, key), () => [queryKeys.site(siteId), queryKeys.jobs.site(siteId)], callbacks);
}

/**
 * A template version being published from its reference site (ADR-0047): true
 * while the site's newest capture runs. When it ends the templates are read
 * again, and then `onDone` hears how it went — once, and only for a capture
 * seen running here.
 */
export function useTemplatePublishing(siteId: string | null, onDone: (job: JobDetail) => void): boolean {
  const jobs = useSiteJobs(siteId ?? '');
  const queryClient = useQueryClient();
  const capture = jobs.data?.items.find((job) => job.type === 'template.capture') ?? null;
  const active = capture !== null && !TERMINAL_JOB_STATES.includes(capture.state);
  const watched = useRef<string | null>(null);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    if (!capture) return;
    if (active) {
      watched.current = capture.id;
      return;
    }
    if (watched.current !== capture.id) return;
    watched.current = null;
    void queryClient.invalidateQueries({ queryKey: queryKeys.templates.all }).then(() => done.current(capture));
  }, [capture, active, queryClient]);
  return active;
}

export function useCaptureTemplate(siteId: string, callbacks?: MutationCallbacks<EnqueueResult, { input: CaptureTemplateRequest; key: string }>) {
  return useApiMutation(
    ({ input, key }) => sitesApi.captureTemplate(siteId, input, key),
    () => [queryKeys.templates.all, queryKeys.jobs.site(siteId), queryKeys.site(siteId)],
    callbacks,
  );
}
